/**
 * Messages Tab — Student workspace Messages view
 * Dummy conversations + Ligio CTA. No backend data dependency.
 * Extracted from StudentDashboardBeta (Phase 3.3e).
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

// ═══════════════════════════════════════════════════════════
// Dummy data
// ═══════════════════════════════════════════════════════════

const dummyConversations = [
  { initial: 'S', name: 'Sarah Mitchell', preview: 'Great progress on paradiddles!', time: 'Today · 2:15 PM', color: 'var(--color-cyan)', active: true },
  { initial: 'M', name: 'Mark Johnson', preview: 'Recital registration confirmed', time: 'Yesterday', color: 'var(--color-amber-accent)', active: false },
  { initial: 'A', name: 'Admin Team', preview: 'New studio policies for July', time: 'Jun 24', color: 'var(--color-teal)', active: false },
]

const dummyMessages = [
  { text: 'Hi Rodrigo! Great work on paradiddles this week. Your stick control has really improved — I can hear the difference in your transitions.', sent: false },
  { text: 'Thank you Sarah! I\'ve been practicing daily. The metronome drill really helps.', sent: true },
  { text: 'Excellent. Let\'s focus on dynamics next session — your speed is solid, now we\'ll add expression. Bring your paradiddle sheet and we\'ll layer in accents.', sent: false },
]

// ═══════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════

const MessagesTab: React.FC = () => {
  const navigate = useNavigate()
  const [activeConv, setActiveConv] = useState(0)

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-[14px] min-h-[480px]">
      {/* Conversation List */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                      rounded-[var(--radius-r)] overflow-y-auto shadow-[var(--shadow-sh0)] max-h-[480px] md:max-h-none">
        {dummyConversations.map((conv, i) => (
          <div key={i}
               onClick={() => setActiveConv(i)}
               className={`flex gap-[10px] p-[12px] border-b border-[var(--color-border-light)]
                          cursor-pointer transition-all duration-[.15s]
                          hover:bg-[var(--color-card2)]
                          ${activeConv === i ? 'bg-[var(--color-elevated)] border-l-[3px] border-l-[var(--color-cyan)]' : ''}`}>
            <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-[15px]
                            font-semibold text-white flex-shrink-0"
                 style={{ background: conv.color }}>{conv.initial}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[var(--color-text-primary)]">{conv.name}</div>
              <div className="text-[11px] text-[var(--color-text-secondary)] overflow-hidden text-ellipsis whitespace-nowrap mt-[1px]">{conv.preview}</div>
              <div className="text-[10px] text-[var(--color-text-tertiary)] mt-[1px]">{conv.time}</div>
            </div>
          </div>
        ))}
        {/* Ligio link */}
        <div className="p-[12px] text-center border-t border-[var(--color-border-light)] mt-[4px]">
          <button
            onClick={() => navigate('/dashboard/ligio')}
            className="px-[16px] py-[8px] bg-[var(--color-teal)] text-white text-[12px] font-semibold
                       rounded-[var(--radius-r-sm)] hover:opacity-90 transition-all duration-[.18s] w-full">
            💬 Open Ligio Messenger
          </button>
          <div className="text-[10px] text-[var(--color-text-tertiary)] mt-[4px]">Full messaging, video calls & more</div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="hidden md:flex flex-col bg-[var(--color-card)] border border-[var(--color-border-light)]
                      rounded-[var(--radius-r)] shadow-[var(--shadow-sh0)] overflow-hidden">
        {/* Chat header */}
        <div className="flex items-center gap-[8px] p-[12px] border-b border-[var(--color-border-light)]">
          <div className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-[14px] font-semibold text-white"
               style={{ background: dummyConversations[activeConv].color }}>
            {dummyConversations[activeConv].initial}
          </div>
          <div className="text-[14px] font-semibold text-[var(--color-text-primary)]">
            {dummyConversations[activeConv].name}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 p-[14px] overflow-y-auto min-h-[280px]">
          {dummyMessages.map((msg, i) => (
            <div key={i}
                 className={`max-w-[72%] p-[9px] rounded-[14px] text-[13px] leading-[1.5] mb-[7px]
                            ${msg.sent
                              ? 'bg-[var(--color-teal)] text-white ml-auto rounded-br-[6px]'
                              : 'bg-[var(--color-card2)] text-[var(--color-text-primary)] mr-auto rounded-bl-[6px]'}`}>
              {msg.text}
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="flex items-center gap-[6px] p-[10px] border-t border-[var(--color-border-light)]">
          <input type="text" placeholder="Type a message..."
                 className="flex-1 bg-[var(--color-card2)] border border-[var(--color-border-light)]
                            rounded-[var(--radius-r-sm)] px-[12px] py-[9px] text-[13px]
                            text-[var(--color-text-primary)] outline-none
                            focus:border-[var(--color-cyan)]" />
          <button className="px-[14px] py-[8px] bg-[var(--color-cyan)] text-white text-[13px] font-semibold
                             rounded-[var(--radius-r-sm)] hover:opacity-90 transition-all duration-[.18s]">
            Send
          </button>
        </div>
      </div>

      {/* Mobile: show only conversation list with Ligio CTA */}
      <div className="md:hidden text-center p-[20px] bg-[var(--color-card)] border border-[var(--color-border-light)]
                      rounded-[var(--radius-r)] shadow-[var(--shadow-sh0)]">
        <div className="text-[48px] mb-[12px]">💬</div>
        <div className="text-[14px] font-bold text-[var(--color-text-primary)] mb-[8px]">
          Messages on mobile
        </div>
        <div className="text-[12px] text-[var(--color-text-tertiary)] mb-[16px]">
          Open Ligio for the full messaging experience with real-time chat and video calls.
        </div>
        <button
          onClick={() => navigate('/dashboard/ligio')}
          className="px-[20px] py-[10px] bg-[var(--color-teal)] text-white text-[13px] font-semibold
                     rounded-[var(--radius-r)] hover:opacity-90 transition-all duration-[.18s]">
          Open Ligio →
        </button>
      </div>
    </div>
  )
}

export default MessagesTab
